import "./pdfs.scss";
import React, { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type Dispatch } from "@reduxjs/toolkit";
import { isEmpty, noop } from "lodash";

import SearchInput from "../../../../components/searchInput/SearchInput";
import ViewType from "../../../../enums/ViewType";
import SortOptions from "../../../../enums/SortOptions";
import Restricted from "../../../Application/Restricted";
import PdfsNoResults from "../../../../views/library/pdfs/overview/PdfsNoResults";
import pdfListUtils from "../../../../utils/pdfListUtils";
import PdfsContainer from "./containers/PdfsContainer";

import { ItemsTypes, RolePermissions, RouteNames, SortingDirection } from "../../../../enums";
import { type Columns, getColumnOptions, sortOptions } from "./getColumnOptions";
import { type RootState } from "../../../Application/globaltypes/redux";
import { type Filters } from "../../../../utils/queryUtils";
import {
  deletePdfs as deletePdfsAction,
  updatePdf as updatePdfAction,
  changeVisibility,
} from "features/Library/Pdfs/state/thunks/overview/pdfsOverviewThunk";
import {
  normalizedPdfsSelector,
  pdfSearchSelector,
  pdfsStateSelector,
  pdfPaginationSelector,
  pdfSortingColumnNameSelector,
  pdfSortingDirectionSelector,
} from "../selectors";
import { type PdfOverview } from "../types/state";
import {
  resetAppliedFilter,
  setAppliedFilter,
  setSearch,
  resetSearch,
  setPagination,
  resetPagination,
  setSortingColumnName,
  resetSortingColumnName,
  setSortingDirection,
  resetSortingDirection,
  setShouldSort,
  resetShouldSort,
} from "../state/slices/pdfFiltersSlice";
import { type NotifyStepSettings, bindAction } from "../../../../interfaces";
import {
  fetchDraftPdfEntity as fetchDraftPdfEntityAction,
  publishPdf as publishPdfAction,
} from "../state/actions/pdfEntityStateActions";
import { reset } from "../state/slices/pdfOverviewSlice";
import { resetTags } from "../state/slices/pdfBaseSlice";
import { PdfOverviewHeader } from "./PdfOverviewHeader/PdfOverviewHeader";
import { CreatePdfButton } from "./PdfOverviewHeader/CreatePdfButton";
import { type CardsViewerItem } from "../../../../components/cardsViewer/types";
import { PdfOverviewCard } from "./Card/PdfOverviewCard";
import { PdfsFilterForm } from "../../../../components/filterForms";
import { PdfModalsContainer } from "../common/PdfModalsContainer";
import { useRtn } from "../../../../hooks/useRtn";
import { type ModalHandlers, type PdfPublishConfirmationParams } from "../types/models";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "../../../../interfaces/onSelectionChanged";
import {
  PdfDiscardSuccess,
  PdfLockSuccess,
  PdfPublishSuccess,
} from "../../../Application/services/realTimeNotification/events/library/libraryEvents";
import { useNavigate } from "react-router";
import Observable from "utils/Observable";
import { AssetVisibilityConfirmationModal } from "components/assetVisibilityConfirmationModal/AssetVisibilityConfirmationModal";
import { fetchOverviewPdfs as fetchOverviewPdfsAction } from "features/Library/Pdfs/state/thunks/overview/fetchOverviewPdfs";

export type PdfsOverviewProps = PropsFromRedux;

// Handling conversion from redux filter name to API filter param
const sortChangeNameMap = {
  modified: SortOptions.ModifiedDateDesc,
  added: SortOptions.CreatedDateDesc,
  title: SortOptions.Title,
  "": "",
} as const;

const publishOrDiscardRtns = [PdfPublishSuccess, PdfDiscardSuccess];
const lockRtns = [PdfLockSuccess];
type UnparsedColumnName = keyof typeof sortChangeNameMap;

export const Pdfs: FC<PdfsOverviewProps> = (props) => {
  const navigate = useNavigate();
  const { updatePdf, publishPdf, deletePdfs, resetOverviewPdfs, fetchOverviewPdfs, fetchDraftPdfEntity } = props;
  const [selectedPdfIds, setSelectedPdfIds] = useState<number[]>([]);
  const [selectedViewType, setSelectedViewType] = useState<ViewType>(ViewType.GRID);
  const [purchasedSelectedCount, setPurchasedSelectedCount] = useState(0);
  const [undeletableSelectedCount, setUndeletableSelectedCount] = useState(0);
  const [hiddenSelectedCount, setHiddenSelectedCount] = useState(0);
  const [visibleSelectedCount, setVisibleSelectedCount] = useState(0);
  const [draftSelectedCount, setDraftSelectedCount] = useState(0);
  const [unpublishedSelectedCount, setUnpublishedSelectedCount] = useState(0);

  const reloadListItemsRef = useRef<((enableSorting: boolean) => void) | undefined>(undefined);
  const modalHandlers = useRef<ModalHandlers>({ delete: () => noop, publish: () => noop, revert: () => noop });
  const isGridView = useMemo(() => selectedViewType === ViewType.GRID, [selectedViewType]);

  /**
   * Handles conversion from sort name ("modified") to
   * an actual valid sort parameter "DateModified desc"
   */
  const parsedSortParameters = useMemo<string>(() => {
    const defaultColumn = props.sortingColumnName?.toLowerCase() || "added";
    return sortChangeNameMap[defaultColumn as UnparsedColumnName];
  }, [props.sortingColumnName]);

  useEffect(() => {
    return () => {
      resetOverviewPdfs();
      if (!window.location.pathname.includes(`/${RouteNames.contentPdfs}`)) props.resetSearchFilters();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePdfCard = useCallback(
    (payload: { id: number }) => {
      updatePdf(payload.id);
    },
    [updatePdf],
  );

  const goToPage = useCallback(
    (id: number, pageName: string) => {
      navigate(`${id}/${pageName}`);
    },
    [navigate],
  );

  const goToEditEntity = useCallback((payload: { id: number }) => goToPage(payload.id, "configuration"), [goToPage]);

  useRtn(publishOrDiscardRtns, updatePdfCard);
  useRtn(lockRtns, goToEditEntity);

  const handleEditClick = useCallback(
    (id: number, isDraft: boolean) => {
      isDraft ? goToPage(id, "configuration") : fetchDraftPdfEntity(id);
    },
    [goToPage, fetchDraftPdfEntity],
  );

  const handlePublishClick = useCallback((params: PdfPublishConfirmationParams) => {
    modalHandlers.current.publish(params);
  }, []);

  const handleDeleteClick = useCallback((ids: number[], flowsCount?: number, packsCount?: number) => {
    modalHandlers.current.delete(ids, flowsCount, packsCount);
  }, []);

  const loadPdfs = (skip?: number, top?: number, sortingColumnName?: string, sortDirection?: SortingDirection) => {
    // Manages if a search is active
    const pdfCardSort = props.shouldSort && props.sortingColumnName ? parsedSortParameters : "";
    isGridView
      ? loadPdfCards(props.pdfs.length, pdfCardSort)
      : loadPdfList(skip, top, sortingColumnName, sortDirection);
  };

  const loadPdfList = (skip?: number, top?: number, sortingColumnName?: string, sortDirection?: SortingDirection) => {
    fetchOverviewPdfs({
      skip,
      top,
      sortBy: pdfListUtils.getSortBy(sortingColumnName as Columns),
      sortDirection,
    });
  };

  const loadPdfCards = useCallback(
    (skip?: number, sortByParams?: string) => {
      const [sortBy, sortDirection] = sortByParams?.split(" ") || [undefined, undefined];
      fetchOverviewPdfs({
        skip,
        top: Number(process.env.REACT_APP_LOAD_ITEMS_COUNT),
        sortBy,
        sortDirection,
        append: true,
      });
    },
    [fetchOverviewPdfs],
  );

  const onSortChange = async (_: React.SyntheticEvent<HTMLElement>, data: { value?: SortOptions }) => {
    resetOverviewPdfs();
    clearSelection();

    const gridViewSort = {
      [SortOptions.ModifiedDateDesc]: () => onSortingColumnNameChange("modified"),
      [SortOptions.CreatedDateDesc]: () => onSortingColumnNameChange("added"),
      [SortOptions.Title]: () => onSortingColumnNameChange("title"),
    };
    // Updates sort by prop
    gridViewSort[data.value as keyof typeof gridViewSort]();
    loadPdfCards(0, data.value);
  };

  const applyFilter = (filter: Filters) => {
    clearSelection();
    props.applyFilter(filter);
    if (isGridView) {
      // Manages if a search is active
      const pdfCardSort = props.shouldSort && props.sortingColumnName ? parsedSortParameters : "";
      resetOverviewPdfs();
      loadPdfCards(0, pdfCardSort);
    }
  };

  const resetFilter = () => {
    clearSelection();
    props.resetFilter();
    if (isGridView) {
      resetOverviewPdfs();
      loadPdfCards();
    }
  };

  const clearSelection = () => {
    setSelectedPdfIds([]);
    setPurchasedSelectedCount(0);
    setUndeletableSelectedCount(0);
    setHiddenSelectedCount(0);
    setVisibleSelectedCount(0);
    setDraftSelectedCount(0);
    setUnpublishedSelectedCount(0);
  };

  /* istanbul ignore next */
  const onSelectionChanged = (args: SelectionChangedArgs<PdfOverview>) => {
    let purchasedCount = 0;
    let undeletableCount = 0;
    let draftCount = 0;
    let hiddenCount = 0;
    let visibleCount = 0;
    let unpublishedCount = 0;

    const onAdded = (pdf: PdfOverview) => {
      pdf.isPurchased && ++purchasedCount;
      !pdf.canBeDeleted && ++undeletableCount;
      pdf.isDraft && ++draftCount;
      !pdf.visibility && ++hiddenCount;
      pdf.visibility && ++visibleCount;
      !pdf.hasBeenPublished && ++unpublishedCount;
    };
    const onRemoved = (pdf: PdfOverview) => {
      pdf.isPurchased && --purchasedCount;
      !pdf.canBeDeleted && --undeletableCount;
      pdf.isDraft && --draftCount;
      !pdf.visibility && --hiddenCount;
      pdf.visibility && --visibleCount;
      !pdf.hasBeenPublished && --unpublishedCount;
    };

    HandleOnSelectionChanged(args, onAdded, onRemoved, clearSelection);

    setPurchasedSelectedCount(purchasedSelectedCount + purchasedCount);
    setUndeletableSelectedCount(undeletableSelectedCount + undeletableCount);
    setDraftSelectedCount(draftSelectedCount + draftCount);
    setHiddenSelectedCount(hiddenSelectedCount + hiddenCount);
    setVisibleSelectedCount(visibleSelectedCount + visibleCount);
    setUnpublishedSelectedCount(unpublishedSelectedCount + unpublishedCount);
  };

  const onViewTypeChange = (viewType: ViewType) => {
    resetOverviewPdfs();
    setSelectedViewType(viewType);
    clearSelection();
    if (viewType === ViewType.GRID) {
      loadPdfCards(0, parsedSortParameters);
    }
  };

  const createReloadListMethod = (reloadListItems: (enableSorting: boolean) => void) => {
    reloadListItemsRef.current = reloadListItems;
  };

  const onPaginationChange = (page: number) => props.setPagination(page);
  const onSortingColumnNameChange = (name: string) => props.setSortingColumnName(name);
  const onSortingDirectionChange = (direction: SortingDirection) => props.setSortingDirection(direction);

  const refetchItemsFromStart = useCallback(
    ({ search, sortBy }: { search?: string; sortBy?: string }) => {
      if (isGridView) {
        resetOverviewPdfs();
        loadPdfCards(0, sortBy);
      } else {
        // @ts-ignore
        reloadListItemsRef.current?.(isEmpty(search));
      }
    },
    [isGridView, resetOverviewPdfs, loadPdfCards],
  );

  const onSearchChange = (search: string) => {
    const shouldSort = search === "";
    props.setSearch(search);
    props.setShouldSort(shouldSort);

    setTimeout(() => {
      refetchItemsFromStart({
        search,
        sortBy: shouldSort && parsedSortParameters ? parsedSortParameters : "",
      });
    }, 0);
  };

  const onPublishConfirm = useCallback(
    (id: number, notificationSettings?: NotifyStepSettings, notifyTypes?: number[]) => {
      publishPdf(id, notificationSettings, notifyTypes);
    },
    [publishPdf],
  );

  const onDeleteConfirm = useCallback(
    (ids: number[]) => {
      // Manages if a search is active
      const pdfCardSort = props.shouldSort && props.sortingColumnName ? parsedSortParameters : "";
      deletePdfs(ids, () => refetchItemsFromStart({ sortBy: pdfCardSort, search: props.search }));
      clearSelection();
    },
    [props.shouldSort, props.sortingColumnName, props.search, parsedSortParameters, deletePdfs, refetchItemsFromStart],
  );

  const publishModal = useMemo(() => {
    return {
      acceptHandler: (handler: (params: PdfPublishConfirmationParams) => void) =>
        (modalHandlers.current.publish = handler),
      onConfirm: onPublishConfirm,
      onClose: clearSelection,
    };
  }, [onPublishConfirm]);

  const deleteModal = useMemo(() => {
    return {
      acceptHandler: (handler: (ids: number[]) => void) => (modalHandlers.current.delete = handler),
      onConfirm: onDeleteConfirm,
      onClose: clearSelection,
    };
  }, [onDeleteConfirm]);

  const onTriggerChangeVisibilityObserver = useMemo(
    () => new Observable<(onConfirm: () => void, visible: boolean, ids: number[]) => void>(),
    [],
  );

  const handleToggleVisibility = (ids: number[], visible: boolean) => () => {
    const pdfCardSort = props.shouldSort && props.sortingColumnName ? parsedSortParameters : "";
    onTriggerChangeVisibilityObserver.notify(
      () => {
        props.toggleVisibility(ids, visible, () => {
          clearSelection();
          refetchItemsFromStart({ sortBy: pdfCardSort, search: props.search });
        });
      },
      visible,
      ids,
    );
  };

  const hideDisabled = !props.userPermissions.includes(RolePermissions.AssetsCreate);

  return (
    <section className="nested-content pdfs">
      <PdfOverviewHeader
        purchasedSelected={purchasedSelectedCount > 0}
        undeletableSelected={undeletableSelectedCount > 0}
        hiddenSelected={hiddenSelectedCount > 0}
        visibleSelected={visibleSelectedCount > 0}
        draftSelected={draftSelectedCount > 0}
        selectedIds={selectedPdfIds}
        pdfDeletionHandler={modalHandlers.current.delete}
        clearSelection={clearSelection}
        onToggleVisibility={handleToggleVisibility}
        unpublishedSelected={unpublishedSelectedCount > 0}
      />
      <PdfModalsContainer publishModal={publishModal} deleteModal={deleteModal} />
      <Restricted
        permissions={[RolePermissions.AssetsCreate, RolePermissions.AssetsManage]}
        renderContent={(hasAnyPermission) => (
          <PdfsContainer
            blur
            items={props.pdfs}
            viewType={selectedViewType}
            itemsType={ItemsTypes.Pdf}
            sortOptions={sortOptions}
            onSortChange={onSortChange}
            fetchData={loadPdfs}
            isLoading={props.changingEntityState || props.arePdfsLoading}
            // @ts-ignore
            getFilterForm={() => <PdfsFilterForm />}
            resetFilter={resetFilter}
            applyFilter={applyFilter}
            onSelectionChanged={onSelectionChanged}
            onSelectedGridItemsChanged={setSelectedPdfIds}
            onSelectedItemChanged={setSelectedPdfIds}
            selectedIds={selectedPdfIds}
            renderCard={(props: CardsViewerItem<PdfOverview>) => {
              return (
                <PdfOverviewCard
                  {...props}
                  handleEditClick={handleEditClick}
                  handlePublishClick={handlePublishClick}
                  handleDeleteClick={handleDeleteClick}
                  onToggleVisibility={handleToggleVisibility}
                  readonly={!hasAnyPermission}
                  hideDisabled={hideDisabled}
                />
              );
            }}
            onViewTypeChange={onViewTypeChange}
            orderBy={props.shouldSort ? parsedSortParameters : ""}
            // @ts-ignore
            sortingDirection={props.shouldSort ? props.sortingDirection || SortingDirection.Descending : ""}
            defaultSortingColumnName={props.shouldSort ? props.sortingColumnName : ""}
            onLoad={isGridView ? loadPdfs : undefined}
            isSelectDisabled={() => !hasAnyPermission}
            setReloadListItems={createReloadListMethod}
            columnOptions={getColumnOptions({
              handleEditClick,
              handlePublishClick,
              handleDeleteClick,
              onToggleVisibility: handleToggleVisibility,
              readonly: !hasAnyPermission,
              hideDisabled,
            })}
            renderSearch={() => (
              <SearchInput placeholder="Search for PDFs..." onChange={onSearchChange} defaultValue={props.search} />
            )}
            noResultsContent={
              <PdfsNoResults filtered={props.filtered || !isEmpty(props.search)} createButton={<CreatePdfButton />} />
            }
            permissions={[RolePermissions.AssetsManage]}
            setPagination={onPaginationChange}
            pagination={props.pagination}
            setSortingColumnName={onSortingColumnNameChange}
            setSortingDirection={onSortingDirectionChange}
          />
        )}
      />
      <AssetVisibilityConfirmationModal
        observer={onTriggerChangeVisibilityObserver}
        assetType="PDF"
        onClose={clearSelection}
      />
    </section>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const pdfsState = pdfsStateSelector(state);
  const base = pdfsState.base;
  const overview = pdfsState.overview;

  return {
    pdfs: overview.pdfs.items,
    search: pdfSearchSelector(state),
    pagination: pdfPaginationSelector(state),
    sortingColumnName: pdfSortingColumnNameSelector(state) as UnparsedColumnName | undefined,
    sortingDirection: pdfSortingDirectionSelector(state),
    shouldSort: overview.filters.shouldSort,
    arePdfsLoading: overview.pdfs.isLoading,
    filtered: !isEmpty(overview.filters.appliedFilter),
    entityId: base.pdfEntityStateReducer.entityId,
    changingEntityState: base.pdfEntityStateReducer.changingEntityState,
    normalizedPdfs: normalizedPdfsSelector(state),
    userPermissions: state.userProfile.permissions,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchOverviewPdfs: bindAction(fetchOverviewPdfsAction, dispatch),
    setSearch: bindAction(setSearch, dispatch),
    setPagination: bindAction(setPagination, dispatch),
    setSortingColumnName: bindAction(setSortingColumnName, dispatch),
    setSortingDirection: bindAction(setSortingDirection, dispatch),
    setShouldSort: bindAction(setShouldSort, dispatch),
    fetchDraftPdfEntity: bindAction(fetchDraftPdfEntityAction, dispatch),
    publishPdf: bindAction(publishPdfAction, dispatch),
    deletePdfs: bindAction(deletePdfsAction, dispatch),
    resetOverviewPdfs: bindAction(reset, dispatch),
    updatePdf: bindAction(updatePdfAction, dispatch),
    applyFilter: bindAction(setAppliedFilter, dispatch),
    resetFilter: bindAction(resetAppliedFilter, dispatch),
    toggleVisibility: bindAction(changeVisibility, dispatch),

    resetSearchFilters: () => {
      dispatch(reset());
      dispatch(resetTags());
      dispatch(resetSearch());
      dispatch(resetPagination());
      dispatch(resetAppliedFilter());
      dispatch(resetSortingColumnName());
      dispatch(resetSortingDirection());
      dispatch(resetShouldSort());
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(Pdfs);
export default ConnectedComponent;
